/**
 * @class WipeSlideshow
 * Generic full-bleed slideshow with a straight clip-path "wipe" transition
 * (same technique as the Academy Color Slider, minus the diagonal skew).
 * Works on any <wipe-slideshow> that contains `.carousel__slide` children
 * and (optionally) `.flickity-prev` / `.flickity-next` nav buttons — used by
 * Academy Hero and Academy Upgrade Hero.
 */
if (!customElements.get('wipe-slideshow')) {
  class WipeSlideshow extends HTMLElement {
    connectedCallback() {
      var root = this;
      var slides = Array.from(root.querySelectorAll(':scope > .carousel__slide'));
      if (!slides.length) return;

      slides[0].classList.add('is-active');

      if (slides.length < 2) return;

      var prevBtns = Array.from(root.querySelectorAll('.flickity-prev'));
      var nextBtns = Array.from(root.querySelectorAll('.flickity-next'));
      var defaultDuration = parseInt(root.dataset.autoplay, 10) || 0;
      var SLIDE_DUR = 1; // seconds — matches the Color Slider's wipe duration
      var current = 0;
      var busy = false;
      var timer = null;
      var hovering = false;

      function clearTimer() {
        if (timer) {
          clearTimeout(timer);
          timer = null;
        }
      }

      function scheduleNext() {
        clearTimer();
        if (hovering) return;
        var duration = parseInt(slides[current].dataset.duration, 10) || defaultDuration;
        if (!duration) return;
        timer = setTimeout(function () { goTo(current + 1, 1); }, duration);
      }

      function playVideos(slide) {
        slide.querySelectorAll('video').forEach(function (v) {
          v.play().catch(function () {});
        });
      }

      function pauseVideos(slide) {
        slide.querySelectorAll('video').forEach(function (v) { v.pause(); });
      }

      function goTo(idx, dir) {
        if (busy) return;
        var total = slides.length;
        idx = ((idx % total) + total) % total;
        if (idx === current) return;

        busy = true;
        var from = slides[current];
        var to = slides[idx];

        if (typeof gsap === 'undefined') {
          from.classList.remove('is-active');
          to.classList.add('is-active');
          pauseVideos(from);
          playVideos(to);
          current = idx;
          busy = false;
          scheduleNext();
          return;
        }

        gsap.set(to, { visibility: 'visible', zIndex: 2, willChange: 'clip-path' });
        gsap.set(from, { zIndex: 1, willChange: 'clip-path' });
        playVideos(to);

        var tl = gsap.timeline({
          // power2.out — starts at full speed, decelerates towards the end
          defaults: { duration: SLIDE_DUR, ease: 'power2.out' },
          onComplete: function () {
            from.classList.remove('is-active');
            to.classList.add('is-active');
            gsap.set(from, { visibility: 'hidden', zIndex: 0, clearProps: 'clipPath,willChange' });
            gsap.set(to, { zIndex: 1, clearProps: 'clipPath,willChange' });
            pauseVideos(from);
            busy = false;
            scheduleNext();
          }
        });

        if (dir > 0) {
          tl.fromTo(to,   { clipPath: 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)' }, { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)' });
          tl.fromTo(from, { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)' },     { clipPath: 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)' }, '<');
        } else {
          tl.fromTo(to,   { clipPath: 'polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)' },         { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)' });
          tl.fromTo(from, { clipPath: 'polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)' },     { clipPath: 'polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)' }, '<');
        }

        current = idx;
      }

      prevBtns.forEach(function (b) { b.addEventListener('click', function () { goTo(current - 1, -1); }); });
      nextBtns.forEach(function (b) { b.addEventListener('click', function () { goTo(current + 1, 1); }); });

      root.addEventListener('mouseenter', function () { hovering = true; clearTimer(); });
      root.addEventListener('mouseleave', function () { hovering = false; scheduleNext(); });

      scheduleNext();

      // Skipped in the theme editor: AJAX section swaps and the device-
      // preview iframe resize don't behave like real page scroll/resize, so
      // this viewport-based pause/resume logic would just as easily leave a
      // video stuck paused there (see academy-video-coordinator.js for the
      // same reasoning). Storefront-only optimization.
      var isDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode;

      // Hero background video keeps decoding/playing even while scrolled
      // fully out of view (unlike the compositor `will-change` layers this
      // is a real decoder cost, see academy-video-coordinator.js). Pause the
      // active slide's video once the slideshow leaves the viewport, resume
      // it once it does — the generous rootMargin starts it again just
      // before it's back on screen rather than exactly on re-entry.
      if (!isDesignMode && 'IntersectionObserver' in window) {
        var videoIO = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) playVideos(slides[current]);
            else pauseVideos(slides[current]);
          });
        }, { rootMargin: '600px 0px' });
        videoIO.observe(root);
      }

      // A desktop↔mobile breakpoint crossing (resize/rotation) swaps which
      // of the active slide's video elements is `display: none` (see
      // academy-video-coordinator.js's offsetParent-based pause) but fires
      // no 'play'/'pause' event of its own — without this, the newly-visible
      // one would stay frozen on its poster and the newly-hidden one would
      // keep decoding. Re-checks the active slide only, debounced.
      if (!isDesignMode) {
        var resyncTimer = null;
        window.addEventListener('resize', function () {
          clearTimeout(resyncTimer);
          resyncTimer = setTimeout(function () {
            Array.prototype.forEach.call(slides[current].querySelectorAll('video'), function (v) {
              if (v.offsetParent === null) { if (!v.paused) v.pause(); }
              else if (v.paused) { v.play().catch(function () {}); }
            });
          }, 200);
        });
      }
    }
  }

  customElements.define('wipe-slideshow', WipeSlideshow);
}
